import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useGoals } from "@/hooks/goals/useGoals";
import { NewResourceCard } from "@/components/cards/NewResourceCard";
import { GoalCard } from "@/components/cards/goals/GoalCard";
import { PATHS, PATH_LABELS, PATH_LABEL_KEYS } from "@/config/paths";
import { SCHEMA_NAMES } from "instantdb/types";
import { DNDSortContainer } from "@/lib/DNDSortContainer";
import { useMobile } from "@/providers/mobileProvider";
import { useMobileNavbar } from "@/providers/mobileNavbarProvider";
import { NotFound } from "@/not-found";
import { GlobalError } from "@/global-error";
import { Container } from "@/components/ui/container";
import { GoalsByHealthArea } from "@/components/dashboard/GoalsByHealthArea";
import { useSortMode } from "@/hooks/routines/useSortMode";
import { SortDropdown } from "@/components/dashboard/SortDropdown";
import { useTranslation } from "@/hooks/useTranslation";
import { seo } from "@/utilities/seo";
import { Heading } from "@/components/ui/heading";

export const Route = createFileRoute("/dashboard/goals")({
  component: GoalsPage,
  notFoundComponent: NotFound,
  errorComponent: GlobalError,
  head: () => ({
    meta: [
      {
        title: `${PATH_LABELS[PATHS.ROUTINES]} - ProgressMade.ai`,
      },
      ...seo({
        title: `${PATH_LABELS[PATHS.ROUTINES]} - ProgressMade.ai`,
        description: "Manage and track your health goals.",
      }),
    ],
  }),
});

function GoalsPage() {
  const { isMobile } = useMobile();
  const { goals, handleCreateGoal, isLoading } = useGoals();
  const { setMobileNavbar } = useMobileNavbar();
  const { sortMode } = useSortMode();
  const { t } = useTranslation();

  // Set up navbar configuration
  useEffect(() => {
    if (isMobile) {
      setMobileNavbar({
        title: t(PATH_LABEL_KEYS[PATHS.GOALS]),
        position: "top",
        navbarChildren: (
          <div className="ml-auto">
            <SortDropdown />
          </div>
        ),
      });
    }
  }, [isMobile, setMobileNavbar]);

  if (isLoading) return null;

  return (
    <Container>
      {!isMobile && (
        <div className="col-span-12 flex items-center gap-2">
          <Heading level="h2" size="h4" className="tracking-tight select-none">
            {t(PATH_LABEL_KEYS[PATHS.GOALS])}
          </Heading>
          <SortDropdown />
        </div>
      )}

      {sortMode === "manual" ? (
        <DNDSortContainer
          slug={SCHEMA_NAMES.goals}
          resources={goals}
          createResourceCard={
            <NewResourceCard
              type={SCHEMA_NAMES.goals}
              className="col-span-12 @[768px]:col-span-6 @[1024px]:col-span-4"
              onClick={() => handleCreateGoal({})}
            />
          }
          renderOverlay={(goal) => <GoalCard key={goal.id} goal={goal} isDragging isOverlay />}
        >
          {(activeId, goals) =>
            goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                isDragging={activeId === goal.id}
                isOverlay={false}
              />
            ))
          }
        </DNDSortContainer>
      ) : (
        <>
          <GoalsByHealthArea goals={goals} />

          {goals.length === 0 && (
            <NewResourceCard type={SCHEMA_NAMES.goals} onClick={() => handleCreateGoal({})} />
          )}
        </>
      )}
    </Container>
  );
}
